/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import times from 'lodash/times';

import { CardCategory, Grid, ProductCard, Typography, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardBadge from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import ProductCardCourseraPlus from 'bundles/product-card/components/legacy/ProductCardCourseraPlus';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import { convertHitToCardProps } from 'bundles/search-common/components/search-cards/SearchCards';
import { useOwnsCourseraLite, useOwnsCourseraPlus } from 'bundles/search-common/providers/LearnerProvider';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';
import useSearchParamsFromQueryString from 'bundles/search-common/utils/useSearchParamsFromQueryString';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';
import { NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP } from 'bundles/unified-seo-content-common/constants';

type InputProps = {
  title: string;
  searchURL: string;
  hitsPerPage: number;
  searchIndexName: string;
};

export type Props = InputProps;

const styles = {
  container: css`
    ${breakpoints.down('sm')} {
      flex-direction: column;
      flex-wrap: wrap;
      align-content: center;
      margin: 0;
      width: 100%;
    }
  `,
  targetSearchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;

    > * {
      height: inherit;
    }

    ${breakpoints.down('xs')} {
      padding: 0 0 24px 0;
    }
  `,
  title: css`
    margin-bottom: 24px;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

const ProductRecommendations = ({ title, searchIndexName, hitsPerPage, searchURL }: Props) => {
  const isScreenMediumDown = useMediaQuery(breakpoints.down('md'));
  const searchParameters = useSearchParamsFromQueryString(new URL(searchURL).search);
  const { results = [], isLoading: searching } = useSearchQuery({
    hubPageProductSearch: {
      id: 'hub-page-product-recs-search',
      entityType: 'PRODUCTS',
      limit: hitsPerPage,
      maxValuesPerFacet: 100,
      ...searchParameters,
    },
  });
  const ownsCourseraLite = useOwnsCourseraLite();
  const ownsCourseraPlus = useOwnsCourseraPlus();

  if (!searching && !results?.length) {
    return null;
  }

  return (
    <div>
      <Typography css={styles.title} variant="h1semibold" component="p">
        {title}
      </Typography>
      <Grid css={styles.container} container spacing={!isScreenMediumDown ? 16 : undefined}>
        {searching
          ? times(NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP, (i) => (
              <Grid item lg={4} md={12} key={`placeholder-${i}`}>
                <ProductCard
                  data-testid="Product Card Skeleton"
                  loading={true}
                  variant={isScreenMediumDown ? 'list' : 'grid'}
                  productType="COURSE"
                />
              </Grid>
            ))
          : results?.[0]?.elements.map((hit, hitPosition) => {
              if (!isSearchProductHit(hit)) return null;

              const cardProps = convertHitToCardProps(hit, hitPosition, false, false, 0);

              const partners = cardProps.partners
                ? getPartnerNamesAndLogos(cardProps.partners, cardProps.cobrandingEnabled, cardProps.partnerLogos)
                : undefined;

              return (
                <Grid item lg={4} md={12} key={hit.id}>
                  <div css={styles.targetSearchCardWrapper}>
                    <LearningProductCard
                      ImageComponent={
                        <LearningProductCardImage
                          imageUrl={hit.imageUrl || ''}
                          enableFluidWidthForLazyImg={true}
                          prioritizeImageLoading={false}
                          enableLazyLoading={false}
                          imageParams={{
                            ...getImagePropOverrides(partners ?? [], hit.imageUrl ?? ''),
                            fit: 'crop',
                          }}
                        />
                      }
                      linkProps={{
                        href: hit?.url || undefined,
                        component: TrackedLink2,
                      }}
                      trackingProps={{
                        trackedLinkProps: {
                          trackingName: 'article_category_hub_page_product_recs_card',
                          withVisibilityTracking: true,
                          requireFullyVisible: true,
                          data: {
                            objectID: hit.id || undefined,
                            hitPosition,
                            queryID: searchParameters.query,
                            query: searchParameters.query,
                            filtersApplied: searchParameters.facetFilters,
                            indexName: searchIndexName,
                          },
                        },
                      }}
                      badge={
                        <>
                          <CardCategory>
                            <ProductCardCourseraPlus
                              isPartOfCourseraPlus={cardProps?.isPartOfCourseraPlus ?? false}
                              ownsCourseraLite={ownsCourseraLite ?? false}
                              ownsCourseraPlus={ownsCourseraPlus ?? false}
                              productType={cardProps.productType}
                            />
                          </CardCategory>
                          <LearningProductCardBadge
                            customBadge={cardProps?.isPathwayContent ? <PathwayContentBadge /> : undefined}
                            productType={cardProps?.productType}
                          />
                        </>
                      }
                      productDuration={cardProps.productDuration}
                      productDifficultyLevel={cardProps.productDifficultyLevel}
                      avgProductRating={cardProps.avgProductRating}
                      numProductRatings={cardProps.numProductRatings}
                      slug={cardProps.slug}
                      id={cardProps.id}
                      name={cardProps.name || ''}
                      partners={partners}
                      skills={cardProps.skills}
                      statusTags={getProductCardStatusTags(false, cardProps.isCostFree, cardProps.slug)}
                      indexName={searchIndexName}
                      variant={isScreenMediumDown ? 'list' : 'grid'}
                      productType={cardProps.productType}
                    />
                  </div>
                </Grid>
              );
            })}
      </Grid>
    </div>
  );
};

export default ProductRecommendations;
